/// <amd-module name="Build/Common/Library/Legacy/Components/BranchDropdown" />
import { ShelvesetPickerActionHub } from "Build/Common/Library/Legacy/Components/ShelvesetAction";
import { TfvcSourceSelector } from "Build/Common/Library/Legacy/Components/TfvcSourceSelector";
import * as Resources from "Build/Common/Library/Legacy/Resources";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import * as React from "react";
import { GitRepository, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import { IGitRestClient } from "VC/Common/RestClient/Git";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { isGuid } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField, TextFieldFocusTreatmentBehavior } from "VSSUI/TextField";
import { css, getSafeId } from "VSSUI/Util";

const refsHeadsPrefix = "refs/heads/";
const tagsPrefix = "refs/tags/";

export interface IBranchDropdownProps {
    className?: string;
    dropdownMinHeight?: number;
    label?: string;
    ariaLabel?: string;
    repositoryType: string;
    repositoryId: string;
    defaultBranch: string;
    connectionId: string;
    sourceVersion?: string;
    shelvesetName?: string;
    currentCommit?: string;
    pageContext?: IVssPageContext;
    projectId?: string;
    viewCommits?: boolean;
    viewTags?: boolean;
    actionHub?: ShelvesetPickerActionHub;
    isDrodownFullWidth?: boolean;
    description?: string;
    comboBoxMaxWidth?: number;
    comboBoxMinWidth?: number;
    autoFocus?: boolean;
    showBranchPickerOnCommitSelected?: boolean;
    renderAllBranchesPicker?: boolean;
    onBranchSelected?: (branch: string) => void;
    onBranchSelectedForCommit?: (branch: string) => void;
    onVersionSelected?: (branch: string) => void;
    onTagSelected?: (tag: string) => void;
    onShelvesetSelected?: (shelveset: string) => void;
    onError?: (errorMessage: string) => void;
}

interface IBranchDropdownState {
    branches: string[];
}

interface BranchResult {
    branches: string[];
}

export const branchDropdownComboBoxInputSelector = ".ms-ComboBox-Input";

const branchDropdownClassName = "branchDropdown";
const branchDropdownComboBoxClassName = "branchDropdown--combobox";

export class BranchDropdown extends VssComponent<IBranchDropdownProps, IBranchDropdownState> {
    private _externalSourcesComboBoxRef: React.RefObject<any> = React.createRef<any>();
    private _repository: IObservableValue<GitRepository | undefined>;
    private _selectedExternalRef: IObservableValue<string>;
    private _showBranchPickerForCommit: IObservableValue<boolean> = new ObservableValue(false);

    constructor(props: IBranchDropdownProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            branches: []
        };

        if (this._repoIsTfsGit) {
            this._repository = new ObservableValue<GitRepository | undefined>(undefined);
        } else if (this._repoIsExternal) {
            this._selectedExternalRef = new ObservableValue<string>(this.props.defaultBranch);
        }
    }

    public componentDidMount() {
        super.componentDidMount();

        if (this._repoIsExternal && !this._repoIsSvn && this.state.branches.length == 0) {
            this._getBranches();
        } else if (this._repoIsTfsGit) {
            this._fetchTfsGitRepo();
        }
        this._resizeLegacyComboBox();
        this.props.autoFocus &&
            this._externalSourcesComboBoxRef &&
            this._externalSourcesComboBoxRef.current &&
            this._externalSourcesComboBoxRef.current.focus();
    }

    public render(): JSX.Element {
        return (
            <div className={css(this.props.className, branchDropdownClassName)}>
                {this._repoIsTfsGit ? (
                    this._renderVersionDropdown()
                ) : this._repoIsTfvc ? (
                    <div>
                        <TfvcSourceSelector
                            pageContext={this.props.pageContext}
                            projectId={this.props.projectId}
                            label={this.props.label}
                            onVersionSelected={this.props.onVersionSelected}
                            onShelvesetSelected={this.props.onShelvesetSelected}
                            actionHub={this.props.actionHub}
                            sourceVersion={this.props.sourceVersion}
                            shelvesetName={this.props.shelvesetName}
                        />
                    </div>
                ) : (
                    this._getExternalSourceProvider()
                )}
            </div>
        );
    }

    public componentDidUpdate(prevProps: Readonly<IBranchDropdownProps>, prevState: Readonly<IBranchDropdownState>) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.defaultBranch !== this.props.defaultBranch) {
            this._resizeLegacyComboBox();
        }
    }

    private get _repoIsTfsGit() {
        return this.props.repositoryType.toLocaleLowerCase() === RepositoryTypes.TfsGit.toLocaleLowerCase();
    }

    private get _repoIsTfvc() {
        return this.props.repositoryType.toLocaleLowerCase() === RepositoryTypes.TfsVersionControl.toLocaleLowerCase();
    }

    private get _repoIsSvn() {
        return this.props.repositoryType.toLocaleLowerCase() === RepositoryTypes.Svn.toLocaleLowerCase();
    }

    private get _repoIsExternal() {
        return !this._repoIsTfsGit && !this._repoIsTfvc;
    }

    private _getExternalSourceProvider = () => {
        return (
            <div>
                <Observer selectedRef={this._selectedExternalRef}>
                    {(props: { selectedRef: string }) => (
                        <ComboBox
                            className={branchDropdownComboBoxClassName}
                            label={this.props.label}
                            ariaLabel={this.props.ariaLabel}
                            options={this._getComboBoxBranches()}
                            autoComplete={true}
                            allowFreeform={true}
                            onChanged={this._onBranchOptionSelected}
                            onMenuOpen={this._onComboBoxOpen}
                            componentRef={this._externalSourcesComboBoxRef && this._externalSourcesComboBoxRef.current}
                            text={this._getFriendlyName(props.selectedRef)}
                        />
                    )}
                </Observer>
                {this.props.description && <span className="branch-dropdown-description body-s secondary-text">{this.props.description}</span>}
                {this.props.viewCommits && (
                    <FormItem className="branchDropdown--textbox" label={Resources.CommitsLabel}>
                        <TextField
                            value={this.props.currentCommit}
                            onChange={(e, newValue) => {
                                this.props.onVersionSelected && this.props.onVersionSelected(newValue);
                            }}
                            disabled={false}
                        />
                    </FormItem>
                )}
            </div>
        );
    };

    private _renderVersionDropdown(): JSX.Element {
        const labelId = "branch-dropdown";
        const secondaryLabelId = "branch-dropdown-for-commit";
        return (
            <>
                {this.props.label && (
                    <label className="ms-label branchDropdown--label" id={getSafeId(labelId)}>
                        {this.props.label}
                    </label>
                )}
                <Observer repository={this._repository}>
                    {(observerProps: { repository?: GitRepository }) =>
                        observerProps.repository ? (
                            <>
                                {this._getWrappedVersionDropdown(
                                    this._onVersionSelected,
                                    observerProps.repository,
                                    this.props.viewCommits || false,
                                    this.props.viewTags || false,
                                    labelId
                                )}
                                {this.props.description && <span className="body-s margin-top-4 secondary-text">{this.props.description}</span>}
                                <ConditionalChildren renderChildren={this._showBranchPickerForCommit}>
                                    <div className="flex-column flex-grow margin-top-16">
                                        <label className="ms-label branchDropdown--label" id={getSafeId(secondaryLabelId)}>
                                            {Resources.BranchDropdownForCommitLabel}
                                        </label>
                                        {this._getWrappedVersionDropdown(
                                            this._onBranchSelectedForCommit,
                                            observerProps.repository,
                                            false,
                                            false,
                                            secondaryLabelId
                                        )}
                                        <span className="body-s margin-top-4 secondary-text">{Resources.PickBranchForCommitDescription}</span>
                                    </div>
                                </ConditionalChildren>
                            </>
                        ) : (
                            <Spinner />
                        )
                    }
                </Observer>
            </>
        );
    }

    private _getWrappedVersionDropdown = (
        onSelect: (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>) => void,
        repo: GitRepository,
        viewCommits: boolean,
        viewTags: boolean,
        labelId?: string
    ) => {
        return (
            <WrappedComponent
                wrappedType={"ms.vss-code-web.version-dropdown-as-textfield"}
                dependencies={["ms.vss-code-web.version-dropdown"]}
                wrappedProps={{
                    ariaLabelledBy: labelId,
                    focusTreatment: TextFieldFocusTreatmentBehavior.all,
                    initiallySelectedVersion: {
                        version: this.props.defaultBranch,
                        versionOptions: GitVersionOptions.None,
                        versionType: GitVersionType.Branch
                    },
                    isDropdownFullWidth: this.props.isDrodownFullWidth,
                    onSelect,
                    renderAllBranchesPicker: this.props.renderAllBranchesPicker,
                    providerOptions: {
                        repository: repo,
                        viewCommitsPivot: viewCommits,
                        viewMyBranches: true,
                        viewTagsPivot: viewTags
                    }
                }}
            />
        );
    };

    private _onVersionSelected = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>) => {
        if (!item.data || !item.data.versionDescriptor) {
            return;
        }

        const selectedVersion = item.data.versionDescriptor.version;
        const selectedVersionType = item.data.versionDescriptor.versionType; // branch, tag, or commit
        let showBranchPickerForCommit = false;

        if (this.props.onBranchSelected && selectedVersionType === GitVersionType.Branch) {
            this.props.onBranchSelected(selectedVersion);
        } else if (this.props.onTagSelected && selectedVersionType === GitVersionType.Tag) {
            this.props.onTagSelected(selectedVersion);
        } else if (this.props.onVersionSelected && selectedVersionType === GitVersionType.Commit) {
            this.props.onVersionSelected(selectedVersion);
            showBranchPickerForCommit = true;
        }

        if (this.props.showBranchPickerOnCommitSelected) {
            this._updateShowBranchPickerForCommit(showBranchPickerForCommit);
        }
        // Fix for BUG 1880996 - we need minHeight for correct display of empty dropdown in Firefox
        if (this.props.dropdownMinHeight) {
            const dropdown = document.querySelector(".bolt-dropdown-expandable-textfield-input") as HTMLElement;
            if (dropdown) {
                dropdown.style.minHeight = this.props.dropdownMinHeight + "px";
            }
        }
    };

    private _updateShowBranchPickerForCommit = (value: boolean) => {
        // only update observable and trigger re-render when new value is different
        if (this._showBranchPickerForCommit.value !== value) {
            if (value && this.props.onBranchSelectedForCommit) {
                // branch picker defaults to show repo's default branch, so set it in the store
                this.props.onBranchSelectedForCommit(this.props.defaultBranch);
            }

            this._showBranchPickerForCommit.value = value;
        }
    };

    private _onBranchSelectedForCommit = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>) => {
        if (!item.data || !item.data.versionDescriptor) {
            return;
        }

        if (this.props.onBranchSelectedForCommit) {
            this.props.onBranchSelectedForCommit(item.data.versionDescriptor.version);
        }
    };

    private _getFriendlyName = (branch: string): string => {
        if (branch.startsWith(refsHeadsPrefix)) {
            return branch.substr(refsHeadsPrefix.length);
        } else if (branch.startsWith(tagsPrefix)) {
            return branch.substr(tagsPrefix.length);
        }
        return branch;
    };

    private _getComboBoxBranches(): IComboBoxOption[] {
        return this.state.branches.map(branch => {
            const friendlyBranch = this._getFriendlyName(branch);
            return {
                key: friendlyBranch,
                text: friendlyBranch
            };
        });
    }

    private _onBranchOptionSelected = (option?: IComboBoxOption, index?: number, value?: string) => {
        const branch = option ? option.text : value;
        if (branch) {
            if (branch.startsWith(tagsPrefix) && this.props.onTagSelected) {
                this.props.onTagSelected(branch);
            } else if (this.props.onBranchSelected) {
                this.props.onBranchSelected(branch);
            }

            this._selectedExternalRef.value = branch;
        }
    };

    private _getBranches() {
        const dataProviderId = "ms.vss-build-web.git-branch-data-provider";
        const requestParams = {
            connectionId: this.props.connectionId,
            sourceProvider: this.props.repositoryType,
            repository: this.props.repositoryId
        };

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.trackPromise(contributionService.getDataAsync(dataProviderId, requestParams, true)).promise.then(
            (result: BranchResult) => {
                if (result) {
                    this.setState({ branches: result.branches });
                } else {
                    if (this.props.onError) {
                        const ex: IServerException = contributionService.getDataProviderExceptions()[dataProviderId];
                        const message = (ex && ex.message) || Resources.BranchesError;
                        this.props.onError(message);
                    }
                }
            },
            (error: Error) => {
                if (this.props.onError) {
                    const message = (error && error.message) || Resources.BranchesError;
                    this.props.onError(message);
                }
            }
        );
    }

    /** Resize the legacy ComboBox to fit the branch name if minWidth and maxWidth are provided (used for external providers) **/
    private _resizeLegacyComboBox() {
        if (!this._repoIsExternal || !this.props.comboBoxMinWidth || !this.props.comboBoxMaxWidth) {
            return;
        }

        const comboBox = document.querySelector(`.${branchDropdownClassName} .${branchDropdownComboBoxClassName}`) as HTMLElement;
        const input = comboBox && (comboBox.querySelector(branchDropdownComboBoxInputSelector) as HTMLInputElement);
        if (input) {
            const padding = 52;
            const minWidth = this.props.comboBoxMinWidth;
            const maxWidth = this.props.comboBoxMaxWidth;
            input.style.width = "0";
            let requiredWidth = input.scrollWidth;

            // Edge and IE do not correctly report scrollWidth for an input element.
            // However, it will work for a div, so we'll create a temporary one.
            // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/11325194/
            if (!requiredWidth) {
                const div = document.createElement("div");
                div.style.position = "absolute";
                div.style.top = "-10000px";
                div.style.left = "-10000px";
                div.textContent = input.value;
                document.body.appendChild(div);
                requiredWidth = div.offsetWidth || maxWidth;
                document.body.removeChild(div);
            }

            input.style.width = "";
            requiredWidth += padding;
            const comboBoxWidth = Math.max(Math.min(requiredWidth, maxWidth), minWidth);
            comboBox.style.width = comboBoxWidth + "px";
        }
    }

    /** Resize the legacy ComboBox drop-down list to the full maxWidth if provided (used for external providers) **/
    private _onComboBoxOpen = () => {
        if (!this.props.comboBoxMaxWidth) {
            return;
        }

        const dropdown = document.querySelector(".ms-Callout.ms-ComboBox-callout") as HTMLElement;
        if (dropdown) {
            dropdown.style.minWidth = this.props.comboBoxMaxWidth + "px";
        }
    };

    private _fetchTfsGitRepo(): void {
        // load the vc-common contribution if it wasn't included
        // if it was already loaded, this won't result in a network call
        this.trackPromise(this._getVcCodeContribution()).promise.then(() => {
            const gitRestClient = this.context.pageContext.getRestClient<IGitRestClient>("IGitRestClient");

            // If the repositoryId is a friendly name instead the GUID for fetching cross-project, then we need to scope to a project.
            const project = isGuid(this.props.repositoryId) ? undefined : this.props.projectId;
            gitRestClient
                .getRepository(this.props.repositoryId, project)
                .then((repository: GitRepository) => {
                    this._repository.value = repository;
                })
                .catch(() => this.props.onError && this.props.onError(Resources.FailedToLoadRepoErrorMessage));
        });
    }

    private _getVcCodeContribution(): Promise<void> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getContributionAsync("ms.vss-code-web.common-content");
    }
}
